import { useEffect, useMemo, useState } from "react";
import api from "../../utils/axiosInstance";
import Button from "../Button/Button";
import { toast } from "../Toast/toast";
import { PAYMENT_PARTIES, fmtNum, fmtDate } from "./paymentParties";

// Mirrors the server: kept links keep their amount, removed ones free theirs,
// and each newly ticked invoice takes what is due on it, oldest first.
const previewAllocation = (invoices, checked, paymentAmount) => {
  const kept = invoices.filter((inv) => inv.linked && checked.has(inv.id));
  let remaining = paymentAmount - kept.reduce((sum, inv) => sum + inv.linked_amount, 0);
  const result = new Map(kept.map((inv) => [inv.id, inv.linked_amount]));
  for (const inv of invoices) {
    if (inv.linked || !checked.has(inv.id)) continue;
    const amount = Math.max(0, Math.min(inv.due_amount, remaining));
    result.set(inv.id, amount);
    remaining -= amount;
  }
  return { result, remaining: Math.max(0, remaining) };
};

const LinkInvoicesModal = ({ party, payment, currency, onClose, onSaved }) => {
  const config = PAYMENT_PARTIES[party];
  const [invoices, setInvoices] = useState([]);
  const [checked, setChecked] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get(`${config.endpoint}/${payment.id}/linkable-invoices`, { withCredentials: true })
      .then(({ data }) => {
        const rows = data.data || [];
        setInvoices(rows);
        setChecked(new Set(rows.filter((r) => r.linked).map((r) => r.id)));
      })
      .catch(() => toast.error("Could not load the invoices."))
      .finally(() => setLoading(false));
  }, [config, payment.id]);

  const amount = Number(payment.amount || 0);
  const { result, remaining } = useMemo(
    () => previewAllocation(invoices, checked, amount),
    [invoices, checked, amount]
  );

  const toggle = (inv) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(inv.id)) {
        next.delete(inv.id);
        return next;
      }
      if (!inv.linked && remaining <= 0) {
        toast.error("This payment has no unlinked amount left. Untick another invoice first.");
        return prev;
      }
      next.add(inv.id);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const invoiceIds = invoices.filter((inv) => checked.has(inv.id)).map((inv) => inv.id);
      const { data } = await api.put(
        `${config.endpoint}/${payment.id}/links`,
        { invoice_ids: invoiceIds },
        { withCredentials: true }
      );
      toast.success("Linked invoices updated.");
      onSaved(data.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to update the linked invoices.");
    } finally {
      setSaving(false);
    }
  };

  const headers = ["", "Code", "Issued at", "Total", "Write off", "Paid", "Due", "Covered by this payment"];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4">
      <div
        className="w-full max-w-4xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-pop"
        role="dialog"
        aria-modal="true"
      >
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold tracking-tight text-gray-900">
            Link invoices to payment {payment.payment_code}
          </h2>
          <p className="mt-1 text-[13px] text-gray-500">
            Tick the invoices this payment covers. Each new invoice takes what is due on it, up to the
            unlinked amount. Untick an invoice to unlink it.
          </p>
        </div>

        <div className="px-5 py-3 text-xs text-gray-700 flex flex-wrap gap-x-6 gap-y-1 border-b border-gray-100">
          <span>Payment: <strong>{fmtNum(amount)} {currency}</strong></span>
          <span>Linked: <strong>{fmtNum(amount - remaining)}</strong></span>
          <span>Unlinked: <strong>{fmtNum(remaining)}</strong></span>
        </div>

        <div className="max-h-[420px] overflow-auto">
          <table className="w-full table-auto border-collapse text-xs min-w-[760px]">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b text-[10px] tracking-widest text-gray-500 uppercase">
                {headers.map((h) => (
                  <th key={h} className="px-3 py-2 font-semibold text-center whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={headers.length} className="py-8 text-center text-gray-400">Loading...</td>
                </tr>
              ) : invoices.length === 0 ? (
                <tr>
                  <td colSpan={headers.length} className="py-8 text-center text-gray-400">
                    This {config.partyLabel} has no invoices in {currency || "this currency"} with anything due.
                  </td>
                </tr>
              ) : (
                invoices.map((inv, i) => {
                  const isChecked = checked.has(inv.id);
                  return (
                    <tr
                      key={inv.id}
                      onClick={() => !saving && toggle(inv)}
                      className={`${i % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-brand-50 cursor-pointer`}
                    >
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggle(inv)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={saving}
                          className="h-4 w-4 accent-brand-600"
                        />
                      </td>
                      <td className="px-3 py-2 text-center whitespace-nowrap font-medium text-gray-900">{inv.invoice_code}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-500">{fmtDate(inv.issued_at)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{fmtNum(inv.total)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{fmtNum(inv.write_off_amount)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{fmtNum(inv.paid_amount)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{fmtNum(inv.due_amount)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap font-semibold">
                        {isChecked ? fmtNum(result.get(inv.id)) : "—"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-5 py-3">
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" variant="primary" size="sm" onClick={handleSave} disabled={saving || loading}>
            {saving ? "Updating..." : "Update"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LinkInvoicesModal;
